import React, { useMemo, useState, useCallback, useEffect } from 'react';
import BoardLayout from '../components/kanban/components/BoardLayout';
import {
  Card,
  Table,
  Form,
  Input,
  Button,
  Select,
  Space,
  Descriptions,
  List,
  Typography,
  Divider,
  message,
  Row,
  Col,
  Tag,
  Drawer,
  Empty,
  Statistic,
  Tooltip,
  Badge,
  Popconfirm,
  Modal
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { DEFAULT_USERS, initialEquipmentsInventory } from '../components/kanban/constants';
import { readJson, writeJson, withNamespace } from '../utils/storage';

const { Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const users = DEFAULT_USERS;

// داده‌های ثابت برای Usage History
const staticUsageData = {
  'EQ-2001': [
    { id: 'U-2001-1', date: '2025-01-15', kind: 'Operation', notes: 'نصب کابل در پست برق اصلی', user: 'علی محمدی' },
    { id: 'U-2001-2', date: '2025-02-20', kind: 'Maintenance', notes: 'بازرسی دوره‌ای عایق کابل', user: 'رضا کریمی' },
    { id: 'U-2001-3', date: '2025-03-10', kind: 'Operation', notes: 'استفاده در پروژه توسعه شبکه', user: 'محمد حسینی' }
  ],
  'EQ-2002': [
    { id: 'U-2002-1', date: '2025-01-20', kind: 'Operation', notes: 'نصب سوکت روی کابل فشار قوی', user: 'احمد نوری' },
    { id: 'U-2002-2', date: '2025-03-05', kind: 'Maintenance', notes: 'تعویض قطعات فرسوده سوکت', user: 'مهدی رضایی' }
  ],
  'EQ-2003': [
    { id: 'U-2003-1', date: '2025-02-10', kind: 'Calibration', notes: 'کالیبراسیون ترانسفورماتور جریان', user: 'سارا احمدی' },
    { id: 'U-2003-2', date: '2025-03-15', kind: 'Operation', notes: 'پایش جریان در خط ۲۰ کیلوولت', user: 'فاطمه زارعی' }
  ],
  'EQ-2004': [
    { id: 'U-2004-1', date: '2025-01-25', kind: 'Maintenance', notes: 'تعمیرات اساسی کلید قطع بار', user: 'امیر عباسی' },
    { id: 'U-2004-2', date: '2025-02-28', kind: 'Test', notes: 'تست عملکرد قطع و وصل', user: 'حسین محمدی' }
  ],
  'EQ-2005': [
    { id: 'U-2005-1', date: '2025-02-05', kind: 'Installation', notes: 'نصب بوشینگ روی ترانسفورماتور T-100', user: 'محسن کاظمی' },
    { id: 'U-2005-2', date: '2025-03-20', kind: 'Inspection', notes: 'بازدید فنی بوشینگ', user: 'عباس رحیمی' }
  ],
  'EQ-2006': [
    { id: 'U-2006-1', date: '2025-01-30', kind: 'Configuration', notes: 'تنظیم پارامترهای رله حفاظتی', user: 'زهرا امینی' },
    { id: 'U-2006-2', date: '2025-02-15', kind: 'Operation', notes: 'حفاظت خط ۳۳ کیلوولت', user: 'نرگس قاسمی' },
    { id: 'U-2006-3', date: '2025-03-25', kind: 'Update', notes: 'آپدیت firmware رله', user: 'محمد تقوی' }
  ],
  'EQ-2007': [
    { id: 'U-2007-1', date: '2025-02-12', kind: 'Replacement', notes: 'تعویض باتری UPS اتاق کنترل', user: 'کریم احمدی' },
    { id: 'U-2007-2', date: '2025-03-08', kind: 'Test', notes: 'تست Backup Time', user: 'محمدرضا علی‌زاده' }
  ],
  'EQ-2008': [
    { id: 'U-2008-1', date: '2025-01-18', kind: 'Installation', notes: 'نصب کنتاکتور برای موتور پمپ آب', user: 'سعید موسوی' },
    { id: 'U-2008-2', date: '2025-03-12', kind: 'Maintenance', notes: 'سرویس دوره‌ای کنتاکتور', user: 'حامد جعفری' }
  ],
  'EQ-2009': [
    { id: 'U-2009-1', date: '2025-02-22', kind: 'Installation', notes: 'نصب ترمینال باکس در محیط انفجاری', user: 'مجید رضوانی' },
    { id: 'U-2009-2', date: '2025-03-18', kind: 'Inspection', notes: 'بازدید اتصالات ترمینال', user: 'علیرضا کمالی' }
  ],
  'EQ-2010': [
    { id: 'U-2010-1', date: '2025-01-28', kind: 'Replacement', notes: 'تعویض فیوز فشار قوی پست A', user: 'حمید نظری' },
    { id: 'U-2010-2', date: '2025-03-05', kind: 'Test', notes: 'تست عملکرد فیوز', user: 'ایمان مرادی' }
  ]
};

const Equipment = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);

  // نمونه داده‌های اولیه
  const initialEquipments = initialEquipmentsInventory

  const STORAGE_NS = 'farscada';
  const EQUIP_KEY = withNamespace(STORAGE_NS, 'equipments');
  const USAGE_KEY = withNamespace(STORAGE_NS, 'equipmentUsage');

  const [equipments, setEquipments] = useState(() => readJson(EQUIP_KEY, initialEquipments));
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [usageMap, setUsageMap] = useState(() => {
    const savedUsage = readJson(USAGE_KEY, {});
    
    // ترکیب داده‌های ذخیره شده با داده‌های ثابت
    const combinedUsage = { ...staticUsageData };
    
    // اضافه کردن داده‌های ذخیره شده (در صورت وجود)
    Object.keys(savedUsage).forEach(key => {
      if (combinedUsage[key]) {
        combinedUsage[key] = [...combinedUsage[key], ...savedUsage[key]];
      } else {
        combinedUsage[key] = savedUsage[key];
      }
    });
    
    return combinedUsage;
  });

  useEffect(() => {
    writeJson(EQUIP_KEY, equipments);
  }, [equipments]);

  useEffect(() => {
    writeJson(USAGE_KEY, usageMap);
  }, [usageMap]);

  const selectedEquipment = useMemo(() =>
    equipments.find(e => e.id === selectedEquipmentId) || null,
    [equipments, selectedEquipmentId]
  );

  const selectedUsage = useMemo(() =>
    usageMap[selectedEquipmentId] || [],
    [usageMap, selectedEquipmentId]
  );

  const latestUsages = useMemo(() => {
    const flatFromStatic = Object.entries(staticUsageData).flatMap(([eqId, arr]) =>
      arr.map(u => ({ ...u, eqId, equipmentName: equipments.find(e => e.id === eqId)?.name || eqId }))
    );
    
    const flatFromSaved = Object.entries(usageMap).flatMap(([eqId, arr]) =>
      arr.map(u => ({ ...u, eqId, equipmentName: equipments.find(e => e.id === eqId)?.name || eqId }))
    );
    
    // ترکیب و مرتب‌سازی همه داده‌ها
    const allUsages = [...flatFromStatic, ...flatFromSaved];
    return allUsages.sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 8);
  }, [usageMap, equipments]);

  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // شبیه‌سازی بارگذاری داده
  const simulateLoading = useCallback(async (duration = 800) => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, duration));
    setLoading(false);
  }, []);

  const handleAddEquipment = async (values) => {
    try {
      setLoading(true);

      const newEq = {
        id: values.id?.trim() || `EQ-${Date.now()}`,
        name: values.name,
        type: values.type,
        location: values.location || '-',
        status: values.status || 'Available',
        value: Number(values.value) || 0,
        description: values.description || '',
        lastMaintenance: values.lastMaintenance || '',
        nextMaintenance: values.nextMaintenance || '',
      };

      if (equipments.some(e => e.id === newEq.id)) {
        message.error('Equipment ID already exists');
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 500)); // شبیه‌سازی API call

      setEquipments(prev => [...prev, newEq]);
      setUsageMap(prev => ({ ...prev, [newEq.id]: [] }));

      form.resetFields();
      setAddOpen(false);
      message.success('Equipment added successfully');
    } catch (error) {
      message.error('Failed to add equipment');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEquipment = async (values) => {
    try {
      setLoading(true);

      await new Promise(resolve => setTimeout(resolve, 500)); // شبیه‌سازی API call

      setEquipments(prev =>
        prev.map(eq =>
          eq.id === selectedEquipmentId
            ? { ...eq, ...values, value: Number(values.value) || 0 }
            : eq
        )
      );

      editForm.resetFields();
      setEditOpen(false);
      message.success('Equipment updated successfully');
    } catch (error) {
      message.error('Failed to update equipment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEquipment = (equipmentId) => {
    confirm({
      title: 'Are you sure you want to delete this equipment?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        setTableLoading(true);
        setTimeout(() => {
          setEquipments(prev => prev.filter(eq => eq.id !== equipmentId));
          setUsageMap(prev => {
            const newMap = { ...prev };
            delete newMap[equipmentId];
            return newMap;
          });

          if (selectedEquipmentId === equipmentId) {
            setSelectedEquipmentId(null);
            setDetailsOpen(false);
          }

          setTableLoading(false);
          message.success('Equipment deleted successfully');
        }, 500);
      },
    });
  };

  const openEditDrawer = (equipment) => {
    setSelectedEquipmentId(equipment.id);
    editForm.setFieldsValue(equipment);
    setEditOpen(true);
  };

  const statusColorMap = {
    Available: 'green',
    'In Use': 'blue',
    Maintenance: 'orange',
    Damaged: 'red',
    Retired: 'default',
  };

  const typeColorMap = {
    Tool: 'blue',
    Instrument: 'purple',
    Safety: 'green',
    Spare: 'orange',
    Electronic: 'volcano',
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 110,
      sorter: (a, b) => a.id.localeCompare(b.id),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: type => <Tag color={typeColorMap[type] || 'default'}>{type}</Tag>,
      filters: [
        { text: 'Tool', value: 'Tool' },
        { text: 'Instrument', value: 'Instrument' },
        { text: 'Safety', value: 'Safety' },
        { text: 'Spare', value: 'Spare' },
        { text: 'Electronic', value: 'Electronic' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      sorter: (a, b) => a.location.localeCompare(b.location),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: status => <Badge color={statusColorMap[status]} text={status} />,
      filters: [
        { text: 'Available', value: 'Available' },
        { text: 'In Use', value: 'In Use' },
        { text: 'Maintenance', value: 'Maintenance' },
        { text: 'Damaged', value: 'Damaged' },
        { text: 'Retired', value: 'Retired' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      width: 120,
      render: value => `$${value}`,
      sorter: (a, b) => a.value - b.value,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEquipmentId(record.id);
                setDetailsOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Edit equipment">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openEditDrawer(record);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete equipment">
            <Popconfirm
              title="Delete equipment"
              description="Are you sure you want to delete this equipment?"
              onConfirm={(e) => {
                e?.stopPropagation();
                handleDeleteEquipment(record.id);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                size="small"
                icon={<DeleteOutlined />}
                danger
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    },
  ];

  const boardStats = useMemo(() => ({
    totalTasks: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    highPriorityTasks: 0,
    overdueTasks: 0,
    completionRate: 0,
  }), []);

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilterType('');
    setFilterStatus('');
  };

  const handleRefreshData = async () => {
    setTableLoading(true);
    await simulateLoading(1000);
    setTableLoading(false);
    message.success('Data refreshed');
  };

  const typeOptions = [
    { value: 'Tool', label: 'Tool' },
    { value: 'Instrument', label: 'Instrument' },
    { value: 'Safety', label: 'Safety' },
    { value: 'Spare', label: 'Spare' },
    { value: 'Electronic', label: 'Electronic' },
  ];

  const statusOptions = [
    { value: 'Available', label: 'Available' },
    { value: 'In Use', label: 'In Use' },
    { value: 'Maintenance', label: 'Maintenance' },
    { value: 'Damaged', label: 'Damaged' },
    { value: 'Retired', label: 'Retired' },
  ];

  const filteredEquipments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return equipments.filter(eq => {
      const matchesSearch = !term ||
        [eq.id, eq.name, eq.type, eq.location, eq.status, eq.description]
          .some(x => String(x).toLowerCase().includes(term));
      const matchesType = !filterType || eq.type === filterType;
      const matchesStatus = !filterStatus || eq.status === filterStatus;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [equipments, searchTerm, filterType, filterStatus]);

  return (
    <BoardLayout
      sidebarCollapsed={sidebarCollapsed}
      setSidebarCollapsed={setSidebarCollapsed}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      filterAssignee={''}
      setFilterAssignee={() => { }}
      filterPriority={''}
      setFilterPriority={() => { }}
      boardStats={boardStats}
      filteredTasks={[]}
      tasks={[]}
      handleClearSearch={handleClearSearch}
      handleAddTask={() => { }}
      handleExportData={() => { }}
      users={users}
      showControls={false}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        {/* Equipment Table */}
        <Card
          title={
            <Space>
              <Text strong>Equipment Inventory</Text>
              <Badge count={filteredEquipments.length} showZero />
            </Space>
          }
          extra={
            <Space wrap>
              <Input
                allowClear
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search equipment..."
                prefix={<SearchOutlined />}
                style={{ width: 260 }}
              />
              <Select
                style={{ width: 160 }}
                value={filterType}
                onChange={v => setFilterType(v)}
                placeholder="Filter by type"
                allowClear
              >
                {typeOptions.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>
              <Select
                style={{ width: 160 }}
                value={filterStatus}
                onChange={v => setFilterStatus(v)}
                placeholder="Filter by status"
                allowClear
              >
                {statusOptions.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>
              <Button onClick={handleClearSearch} disabled={!searchTerm && !filterType && !filterStatus}>
                Clear
              </Button>
              <Tooltip title="Refresh data">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefreshData}
                  loading={tableLoading}
                />
              </Tooltip>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddOpen(true)}
                loading={loading}
              >
                Add Equipment
              </Button>
            </Space>
          }
          bodyStyle={{ padding: 0 }}
        >
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filteredEquipments}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
            }}
            loading={tableLoading}
            locale={{ emptyText: <Empty description="No equipment found" /> }}
            onRow={(record) => ({
              onClick: () => {
                setSelectedEquipmentId(record.id);
                setDetailsOpen(true);
              },
              style: { cursor: 'pointer' }
            })}
            scroll={{ x: 1000 }}
          />
        </Card>

        {/* Latest Usage */}
        <Card
          title="Recent Usage History"
          extra={
            <Text type="secondary">Last 8 activities</Text>
          }
        >
          {latestUsages.length === 0 ? (
            <Empty description="No recent usage" />
          ) : (
            <List
              dataSource={latestUsages}
              renderItem={(item) => (
                <List.Item
                  onClick={() => { setSelectedEquipmentId(item.eqId); setDetailsOpen(true); }}
                  style={{ cursor: 'pointer', padding: '12px 0' }}
                  actions={[
                    <Tag color={typeColorMap[equipments.find(e => e.id === item.eqId)?.type] || 'default'}>
                      {equipments.find(e => e.id === item.eqId)?.type}
                    </Tag>
                  ]}
                >
                  <List.Item.Meta
                    avatar={<Badge status="processing" />}
                    title={
                      <Space>
                        <Text strong>{item.equipmentName}</Text>
                        <Text type="secondary">• {item.kind}</Text>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        <Text>{item.notes}</Text>
                        <Text type="secondary">
                          {item.date} • By {item.user}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Card>

        {/* Equipment Details Drawer */}
        <Drawer
          title={
            <Space>
              <Text strong>Equipment Details</Text>
              {selectedEquipment && (
                <Tag color={statusColorMap[selectedEquipment.status]}>{selectedEquipment.status}</Tag>
              )}
            </Space>
          }
          width={720}
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
          }}
          drawerStyle={{
            background: '#ffffff',
            boxShadow: '-8px 0 24px rgba(0, 0, 0, 0.15)',
          }}
          headerStyle={{
            background: '#ffffff',
            borderBottom: '1px solid #f0f0f0',
          }}
          bodyStyle={{
            background: '#ffffff',
            padding: '24px',
          }}
          extra={
            selectedEquipment && (
              <Space>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => {
                    setDetailsOpen(false);
                    openEditDrawer(selectedEquipment);
                  }}
                >
                  Edit
                </Button>
              </Space>
            )
          }
        >
          {selectedEquipment ? (
            <>
              <Descriptions bordered column={2} size="middle">
                <Descriptions.Item label="ID" span={1}>
                  <Text strong>{selectedEquipment.id}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Name" span={1}>
                  {selectedEquipment.name}
                </Descriptions.Item>
                <Descriptions.Item label="Type">
                  <Tag color={typeColorMap[selectedEquipment.type]}>{selectedEquipment.type}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Badge color={statusColorMap[selectedEquipment.status]} text={selectedEquipment.status} />
                </Descriptions.Item>
                <Descriptions.Item label="Location">
                  {selectedEquipment.location}
                </Descriptions.Item>
                <Descriptions.Item label="Value">
                  <Text strong>${selectedEquipment.value}</Text>
                </Descriptions.Item>
                {selectedEquipment.description && (
                  <Descriptions.Item label="Description" span={2}>
                    {selectedEquipment.description}
                  </Descriptions.Item>
                )}
                {selectedEquipment.lastMaintenance && (
                  <Descriptions.Item label="Last Maintenance">
                    {selectedEquipment.lastMaintenance}
                  </Descriptions.Item>
                )}
                {selectedEquipment.nextMaintenance && (
                  <Descriptions.Item label="Next Maintenance">
                    {selectedEquipment.nextMaintenance}
                  </Descriptions.Item>
                )}
              </Descriptions>

              <Divider />

              <Typography.Title level={5}>Usage History</Typography.Title>
              <Table
                style={{ marginTop: 8 }}
                rowKey="id"
                size="small"
                columns={[
                  { title: 'Date', dataIndex: 'date', width: 120 },
                  { title: 'Type', dataIndex: 'kind', width: 120 },
                  { title: 'Description', dataIndex: 'notes', ellipsis: true },
                  { title: 'User', dataIndex: 'user', width: 100 },
                ]}
                dataSource={selectedUsage}
                pagination={{
                  pageSize: 5,
                  showSizeChanger: false,
                  showTotal: (total) => `${total} records`
                }}
                locale={{ emptyText: <Empty description="No usage history" /> }}
              />
            </>
          ) : (
            <Empty description="No equipment selected" />
          )}
        </Drawer>

        {/* Add Equipment Drawer */}
        <Drawer
          title="Add New Equipment"
          width={520}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          mask={true}
          maskClosable={true}
          destroyOnClose
          style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
          }}
          drawerStyle={{
            background: '#ffffff',
            boxShadow: '-8px 0 24px rgba(0, 0, 0, 0.15)',
          }}
          headerStyle={{
            background: '#ffffff',
            borderBottom: '1px solid #f0f0f0',
          }}
          bodyStyle={{
            background: '#ffffff',
            padding: '24px',
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddEquipment}
            requiredMark="optional"
          >
            <Form.Item
              label="Equipment ID"
              name="id"
              tooltip="Leave empty to auto-generate"
            >
              <Input placeholder="EQ-XXXX" />
            </Form.Item>

            <Form.Item
              label="Equipment Name"
              name="name"
              rules={[{ required: true, message: 'Please enter equipment name' }]}
            >
              <Input placeholder="Enter equipment name" />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
            >
              <Input.TextArea
                placeholder="Equipment description"
                rows={3}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Type"
                  name="type"
                  rules={[{ required: true, message: 'Please select type' }]}
                >
                  <Select placeholder="Select type">
                    {typeOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Status"
                  name="status"
                  initialValue="Available"
                >
                  <Select>
                    {statusOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Location"
              name="location"
            >
              <Input placeholder="e.g., Warehouse A, Workshop" />
            </Form.Item>

            <Form.Item
              label="Value ($)"
              name="value"
              rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: 'Please enter a valid number' }]}
            >
              <Input type="number" placeholder="0.00" step="0.01" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Last Maintenance" name="lastMaintenance">
                  <Input type="date" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Next Maintenance" name="nextMaintenance">
                  <Input type="date" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<PlusOutlined />}
                  loading={loading}
                >
                  Add Equipment
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Drawer>

        {/* Edit Equipment Drawer */}
        <Drawer
          title="Edit Equipment"
          width={520}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          destroyOnClose
        >
          {selectedEquipment && (
            <Form
              form={editForm}
              layout="vertical"
              onFinish={handleEditEquipment}
              initialValues={selectedEquipment}
              requiredMark="optional"
            >
              <Form.Item
                label="Equipment ID"
                name="id"
              >
                <Input disabled />
              </Form.Item>

              <Form.Item
                label="Equipment Name"
                name="name"
                rules={[{ required: true, message: 'Please enter equipment name' }]}
              >
                <Input placeholder="Enter equipment name" />
              </Form.Item>

              <Form.Item
                label="Description"
                name="description"
              >
                <Input.TextArea
                  placeholder="Equipment description"
                  rows={3}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Type"
                    name="type"
                    rules={[{ required: true, message: 'Please select type' }]}
                  >
                    <Select placeholder="Select type">
                      {typeOptions.map(opt => (
                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Status"
                    name="status"
                  >
                    <Select>
                      {statusOptions.map(opt => (
                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Location"
                name="location"
              >
                <Input placeholder="e.g., Warehouse A, Workshop" />
              </Form.Item>

              <Form.Item
                label="Value ($)"
                name="value"
                rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: 'Please enter a valid number' }]}
              >
                <Input type="number" placeholder="0.00" step="0.01" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Last Maintenance" name="lastMaintenance">
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Next Maintenance" name="nextMaintenance">
                    <Input type="date" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Space>
                  <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                  >
                    Update Equipment
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </Drawer>
      </Space>
    </BoardLayout>
  );
};

export default Equipment;